import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import apiService from '../services/apiService';

const FirmwareUpload = () => {
    const navigate = useNavigate();
    const [devices, setDevices] = useState([]);
    const [formData, setFormData] = useState({
        device_id: '',
        version: '',
        release_notes: ''
    });
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadDevices();
    }, []);

    const loadDevices = async () => {
        try {
            const response = await apiService.getDevices();
            if (response.success) {
                setDevices(response.data);
            }
        } catch (error) {
            console.error('Load devices error:', error);
        }
    };

    const onDrop = (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles.length > 0) {
            setError('Invalid file type. Only .bin, .hex, .fw, and .img files are allowed.');
            return;
        }

        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
            setError('');
        }
    };

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop,
        accept: {
            'application/octet-stream': ['.bin', '.hex', '.fw', '.img']
        },
        maxFiles: 1,
        maxSize: 50 * 1024 * 1024 // 50MB
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validation
        if (!formData.device_id) {
            setError('Please select a device type');
            return;
        }

        if (!formData.version.trim()) {
            setError('Please enter a version number');
            return;
        }

        if (!/^\d+\.\d+\.\d+$/.test(formData.version.trim())) {
            setError('Version must follow semantic versioning (e.g., 1.0.0)');
            return;
        }

        if (!file) {
            setError('Please select a firmware file');
            return;
        }

        try {
            setUploading(true);
            setUploadProgress(0);

            const uploadFormData = new FormData();
            uploadFormData.append('firmware', file);
            uploadFormData.append('device_id', formData.device_id);
            uploadFormData.append('version', formData.version.trim());
            uploadFormData.append('release_notes', formData.release_notes.trim());

            const response = await apiService.uploadFirmware(
                uploadFormData,
                (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                }
            );

            if (response.success) {
                setSuccess(`Firmware uploaded successfully: ${response.data.device.name} v${response.data.firmware.version}`);
                
                // Reset form
                setFormData({ device_id: '', version: '', release_notes: '' });
                setFile(null);
                setUploadProgress(0);

                // Navigate to firmware list after a delay
                setTimeout(() => {
                    navigate('/firmware');
                }, 2000);
            } else {
                setError(response.error || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            setError(error.response?.data?.error || error.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Upload Firmware</h1>
                <p className="mt-2 text-gray-600">
                    Upload new firmware files for WattSwap devices
                </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Device Selection */}
                    <div>
                        <label htmlFor="device_id" className="block text-sm font-medium text-gray-700 mb-2">
                            Device Type *
                        </label>
                        <select
                            id="device_id"
                            value={formData.device_id}
                            onChange={(e) => setFormData({...formData, device_id: e.target.value})}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        >
                            <option value="">Select a catalog SKU</option>
                            {devices.map(device => (
                                <option key={device.id} value={device.id}>
                                    {device.name}
                                    {device.sku_name ? ` — ${device.sku_name}` : ''}
                                    {device.platformio_env ? ` (OTA: ${device.platformio_env})` : ''}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                            SKUs come from the device catalog. MQTT publish uses the platformio env as target.
                        </p>
                    </div>

                    {/* Version */}
                    <div>
                        <label htmlFor="version" className="block text-sm font-medium text-gray-700 mb-2">
                            Version *
                        </label>
                        <input
                            type="text"
                            id="version"
                            value={formData.version}
                            onChange={(e) => setFormData({...formData, version: e.target.value})}
                            placeholder="e.g., 1.0.0"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Use semantic versioning (major.minor.patch)
                        </p>
                    </div>

                    {/* File Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Firmware File *
                        </label>
                        <div
                            {...getRootProps()}
                            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                                isDragActive
                                    ? isDragReject
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-primary-500 bg-primary-50'
                                    : 'border-gray-300 hover:border-gray-400'
                            }`}
                        >
                            <input {...getInputProps()} />
                            <div className="space-y-2">
                                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {file ? (
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                        <p className="text-xs text-gray-500">{apiService.formatFileSize(file.size)}</p>
                                    </div>
                                ) : (
                                    <div>
                                        <p className="text-sm text-gray-600">
                                            {isDragActive ? 'Drop the file here' : 'Drag and drop a firmware file here, or click to select'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Supported formats: .bin, .hex, .fw, .img (max 50MB)
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Release Notes */}
                    <div>
                        <label htmlFor="release_notes" className="block text-sm font-medium text-gray-700 mb-2">
                            Release Notes
                        </label>
                        <textarea
                            id="release_notes"
                            rows={4}
                            value={formData.release_notes}
                            onChange={(e) => setFormData({...formData, release_notes: e.target.value})}
                            placeholder="Describe what's new in this firmware version..."
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>

                    {/* Upload Progress */}
                    {uploading && (
                        <div>
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                                <span>Uploading...</span>
                                <span>{uploadProgress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                                <div className="text-red-400">
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-4">
                            <div className="flex">
                                <div className="text-green-400">
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-green-800">{success}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate('/firmware')}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={uploading}
                            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading ? 'Uploading...' : 'Upload Firmware'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FirmwareUpload;